// Where a legend sits on a keycap and how big it is, out of RenderKeycapIcons and
// RenderKeycapTexts (backfill PR 26): the top-left corner of a text or icon in its grid cell
// of the keycap's printable area (one calculation for both renderers, whose copies were the
// same apart from the texts' constant anchor), the SVG transform that mirrors an uploaded
// icon, and the dimensions of a text in its fontkit font, which the texts' bounding boxes,
// baseline and decorations are placed by.
import { normalizeFlip } from './iconEntries';

export const getLegendPosition = (
  position,
  selectedBoundingBoxWidth,
  selectedBoundingBoxHeight,
  printableAreas
) => {
  let posX, posY;

  const { gridStructure, gridRow, gridColumn, cellAlignment, isoSection } =
    position; // Destructure grid and alignment properties from position
  const {
    innerRect1X,
    innerRect1Y,
    innerRect1Width,
    innerRect1Height,
    innerRect2X,
    innerRect2Y,
    innerRect2Width,
    innerRect2Height,
  } = printableAreas; // the keycap's printable rectangles, as KeycapOverlay passes them to the renderers: the second one is the ISO Enter's lower part, null on every other keycap

  let rows, columns;
  switch (gridStructure) {
    case '3x3':
      rows = 3;
      columns = 3;
      break;
    case '3x2':
      rows = 3;
      columns = 2;
      break;
    case '2x3':
      rows = 2;
      columns = 3;
      break;
    case '2x2':
      rows = 2;
      columns = 2;
      break;
    default: // a stored position without one of the four grids the editors write: the whole area as one cell, kept as the renderer's fallback (no design should hold one)
      rows = 1;
      columns = 1;
  } // Determine grid structure (rows and columns)

  const useRect2 =
    isoSection === 'main' &&
    innerRect2X !== null &&
    innerRect2Y !== null &&
    innerRect2Width !== null &&
    innerRect2Height !== null; // Determine which rectangular area to use based on isoSection and Rect 2's validity

  // Select rect properties based on whether Rect 2 is used:
  const rectX = useRect2 ? innerRect2X : innerRect1X;
  const rectY = useRect2 ? innerRect2Y : innerRect1Y;
  const rectWidth = useRect2 ? innerRect2Width : innerRect1Width;
  const rectHeight = useRect2 ? innerRect2Height : innerRect1Height;

  // Calculate width and height of each grid cell:
  const cellWidth = rectWidth / columns;
  const cellHeight = rectHeight / rows;

  // Calculate top-left coordinates of the specified grid cell:
  const cellX = rectX + (gridColumn - 1) * cellWidth;
  const cellY = rectY + (gridRow - 1) * cellHeight;

  switch (cellAlignment) {
    // Align the text or icon within the grid cell based on the cellAlignment property:
    case 'center':
      posX = cellX + cellWidth / 2 - selectedBoundingBoxWidth / 2;
      posY = cellY + cellHeight / 2 - selectedBoundingBoxHeight / 2;
      break;
    case 'topLeft':
      posX = cellX;
      posY = cellY;
      break;
    case 'top':
      posX = cellX + cellWidth / 2 - selectedBoundingBoxWidth / 2;
      posY = cellY;
      break;
    case 'topRight':
      posX = cellX + cellWidth - selectedBoundingBoxWidth;
      posY = cellY;
      break;
    case 'left':
      posX = cellX;
      posY = cellY + cellHeight / 2 - selectedBoundingBoxHeight / 2;
      break;
    case 'right':
      posX = cellX + cellWidth - selectedBoundingBoxWidth;
      posY = cellY + cellHeight / 2 - selectedBoundingBoxHeight / 2;
      break;
    case 'bottomLeft':
      posX = cellX;
      posY = cellY + cellHeight - selectedBoundingBoxHeight;
      break;
    case 'bottom':
      posX = cellX + cellWidth / 2 - selectedBoundingBoxWidth / 2;
      posY = cellY + cellHeight - selectedBoundingBoxHeight;
      break;
    case 'bottomRight':
      posX = cellX + cellWidth - selectedBoundingBoxWidth;
      posY = cellY + cellHeight - selectedBoundingBoxHeight;
      break;
    default: // a stored position without one of the nine alignments the editors write: the cell's centre point, kept as the renderer's fallback (no design should hold one)
      posX = cellX + cellWidth / 2;
      posY = cellY + cellHeight / 2;
  }

  return { x: posX, y: posY }; // Return computed position
}; // Calculate and return the x and y coordinates to position the text or icon within a grid cell (the renderers add the entry's offsets and rotation to the transform)

export const getFlipTransform = (flip, width, height) => {
  const normalizedFlip = normalizeFlip(flip);
  const numericWidth = Number.parseFloat(String(width)) || 0;
  const numericHeight = Number.parseFloat(String(height)) || 0;

  if (normalizedFlip === 'horizontal vertical') {
    return `translate(${numericWidth}, ${numericHeight}) scale(-1, -1)`;
  }

  if (normalizedFlip === 'horizontal') {
    return `translate(${numericWidth}, 0) scale(-1, 1)`;
  }

  if (normalizedFlip === 'vertical') {
    return `translate(0, ${numericHeight}) scale(1, -1)`;
  }

  return '';
}; // creates an SVG transform that mirrors a non-Iconify icon around its own width/height box

export const getGlyphDimensions = (
  font,
  text,
  fontSize,
  letterSpacing, // null while the text spacing is inactive, as the renderer passes it (adding null adds nothing)
  wordSpacing,
  strokeWidth, // null while the stroke is inactive
  variationSettings // {} for a static font: the editor leaves the weight and width null for a font without the axes
) => {
  const fontToUse = Object.keys(variationSettings).length
    ? font.getVariation(variationSettings)
    : font; // Use getVariation (get a version of the font with the variationSettings applied) if there are variation settings; otherwise, use the base font

  const glyphs = fontToUse.glyphsForString(text); // converting the text to an array of glyphs using the font

  const scale = fontSize / fontToUse.unitsPerEm; // Calculate scaling factor to convert internal font units to pixels; the size is the number the text editor writes and the legends model stores (the conversion of a '4px' string went in chain 11, user decision: no caller sends one, and a string would give NaN here)

  // Initialize vertical boundaries to calculate text block height:
  let maxYMax = -Infinity; // Start with the smallest possible value to find the maximum yMax
  let minYMin = Infinity; // Start with the largest possible value to find the minimum yMin

  let leftSpacing = 0; // to store the defaul letter spacing on the left side of the glyph that is being rendered on the most left side
  let rightSpacing = 0; // to store the default letter spacing on the right side of the glyph that is being rendered on the most right side

  let leftmost = null;
  let rightmost = null;
  let currentX = 0;

  glyphs.forEach((glyph, index) => {
    // With negative values for letterSpacing and wordSpacing, glyphs can overlap or reorder visually.
    // Tracking only advance widths won’t work since negative spacing may push parts of the text outside
    // the typical boundaries, even reversing the order of some glyphs. Instead, we track the actual visual
    // boundaries using the leftmost and rightmost points of each glyph, including xMin and xMax, to capture
    // the full width accurately, even when spacing values cause overlapping or reordered glyphs.

    if (glyph.codePoints[0] === 32) {
      // Handle spaces separately since they act as gaps and don’t define visible boundaries.
      // This ensures that spaces don’t alter leftmost and rightmost visual boundaries,
      // which should reflect the text content’s actual visible extent.
      currentX += glyph.advanceWidth * scale; // Move currentX by space's advance width
      currentX += letterSpacing; // Add letter spacing
      currentX += wordSpacing; // Add word spacing
      return; // Skip to the next glyph since spaces don’t impact the visual boundary
    }

    const glyphLeft = currentX + (glyph.bbox.minX * scale || 0); // calculating the position of the left edge of the current glyph
    const glyphRight = currentX + (glyph.bbox.maxX * scale || 0); // calculating the position of the right edge of the current glyph

    if (leftmost === null || glyphLeft < leftmost) {
      // if the current glyphs left edge is further left than the ones before:
      leftmost = glyphLeft; // Set leftmost position

      leftSpacing = glyph.bbox.minX * scale || 0; // Adjust left spacing based on leftmost glyph
    }

    if (rightmost === null || glyphRight > rightmost) {
      // if the current glyphs right edge is further right than the ones before:
      rightmost = glyphRight; // Set rightmost position

      rightSpacing = glyph.advanceWidth * scale - glyph.bbox.maxX * scale || 0; // Adjust right spacing based on rightmost glyph
    }

    currentX += glyph.advanceWidth * scale; // Move current X by the glyph's width

    if (index < glyphs.length - 1) {
      currentX += letterSpacing; // Add letter spacing between glyphs
    }

    // Track max and min Y values for vertical bounds of all glyphs:
    if (glyph.bbox) {
      maxYMax = Math.max(maxYMax, glyph.bbox.maxY * scale); // Set maxY based on glyph
      minYMin = Math.min(minYMin, glyph.bbox.minY * scale); // Set minY based on glyph
    }
  });

  let totalWidth = rightmost - leftmost; // Calculate total width based on visual boundaries
  const totalHeightWithoutStrokeWidth = maxYMax - minYMin; // the totalHeightWithoutStrokeWidth is needed for calculations related to the texts dominantBaseline (in this case the calculation of centralBaselineY). The stroke width does not effect the bounding box and dominant baseline of the text (adding the stroke width to the calculation of distanceFromCentralBaselineToTopOfVisualBoundingBox would result in calculating an incorrecy value)

  const topOfVisualBoundingBox = maxYMax; // Top boundary of visual bounding box

  const centralBaselineY =
    (fontToUse.ascent * scale - Math.abs(fontToUse.descent * scale)) / 2; // Calculate central baseline

  const distanceFromCentralBaselineToTopOfVisualBoundingBox =
    topOfVisualBoundingBox - centralBaselineY; // The difference between the central baseline and the top of the visual bounding box

  const ascenderHeight = fontToUse.ascent * scale; // get the height of the ascender of the font
  const descenderHeight = Math.abs(fontToUse.descent * scale); // get the height of the descender of the font (Descender is negative, so we take absolute value)
  const typographicBoundingBoxHeight = ascenderHeight + descenderHeight; // Use font's ascender and descender to calculate the typographic bounding box (the 'standard' bbox of text)

  const topTypographicBoundingBoxToVisualTopOffset = ascenderHeight - maxYMax; // Calculate offset between top of the typographic bbox and top of visual bbox

  const topFontBoundingBoxToVisualTopOffset =
    fontToUse.bbox.maxY * scale - maxYMax; // calculating offset between top of the font's bbox (which is the highest point the font allows) and the visual bbox

  const distanceFromAlphabeticBaselineToTopOfTypographicBoundingBox =
    ascenderHeight; // Calculate the distance from the top of the typographic bounding box to the alphabetic baseline (the ascender part of a font starts at the alphabetic baseline and goes up to the top of the typographic bbox. The alphabetic baseline is at position 0, which makes the ascender height the distance between the alphabetic baseline and the top of the typographic bbox)

  const distanceFromAlphabeticBaselineToTopOfVisualBoundingBox =
    topOfVisualBoundingBox; // the difference is the height of the visual bbox itself (the alphabetic baseline is the baseline itself, so it's at 0)

  const totalHeight = totalHeightWithoutStrokeWidth + strokeWidth; // Add stroke width to total height
  totalWidth += strokeWidth; // Add stroke width to total width

  const fontBoundingBoxHeight =
    (fontToUse.bbox.maxY - fontToUse.bbox.minY) * scale; // Calculate font's total bounding box height (the maximum vertical space that is needed to place the heighest and lowest glyphs of a font inside)

  const distanceFromAlphabeticBaselineToTopOfFontBoundingBox =
    fontToUse.bbox.maxY * scale; // Difference between alphabetic baseline and the top of the font's bounding box (The alphabetic baseline is the baseline itself, so it's at 0)

  const distanceFromCentralBaselineToTopOfTypographicBoundingBox =
    ascenderHeight - centralBaselineY; // The difference between the central baseline and the top of the typographic bounding box

  const distanceFromCentralBaselineToTopOfFontBoundingBox =
    fontToUse.bbox.maxY * scale - centralBaselineY; // The difference between the central baseline and the top of the font's bounding box

  const distanceFromTopOfTypographicBoundingBoxToTopOfFontBoundingBox =
    fontToUse.bbox.maxY * scale - ascenderHeight; // The difference between the typographic bbox and the top of the font's bounding box

  return {
    totalWidth,
    totalHeight,
    leftSpacing,
    rightSpacing,
    distanceFromCentralBaselineToTopOfVisualBoundingBox,
    typographicBoundingBoxHeight,
    topTypographicBoundingBoxToVisualTopOffset,
    distanceFromAlphabeticBaselineToTopOfTypographicBoundingBox,
    fontBoundingBoxHeight,
    distanceFromAlphabeticBaselineToTopOfVisualBoundingBox,
    distanceFromAlphabeticBaselineToTopOfFontBoundingBox,
    distanceFromCentralBaselineToTopOfTypographicBoundingBox,
    distanceFromCentralBaselineToTopOfFontBoundingBox,
    topFontBoundingBoxToVisualTopOffset,
    distanceFromTopOfTypographicBoundingBoxToTopOfFontBoundingBox,
    leftmost,
  };
}; // Calculate and return detailed glyph dimensions based on font settings and spacing adjustments
